import * as React from 'react';

import styled from 'styled-components';
import Flex from '@components/layout/flex';

import DropdownCtrl, {
  GenericDropdownBuilder,
} from '@webapp/controllers/form/dropdown';
import { Option } from '../../../types/common/option';
import { ReleaseTimePreset } from '@common/types/artist-preset/release-time';

interface PresetReleaseTimeCtrlProps {
  className?: string;
  time?: ReleaseTimePreset;
  onChange: (time: ReleaseTimePreset) => void;
}

const HOURS: Option<number>[] = new Array(12)
  .fill(0)
  .map((_, i) => ({ label: `${i + 1}`.padStart(2, '0'), value: i + 1 }));

const MINUTES: Option<number>[] = new Array(4)
  .fill(0)
  .map((_, i) => ({ label: `${i * 15}`.padStart(2, '0'), value: i * 15 }));

const DAY_PERIODS: Option<ReleaseTimePreset['period']>[] = [
  { label: 'AM', value: 'AM' },
  { label: 'PM', value: 'PM' },
];

const NumberDropdownCtrl = GenericDropdownBuilder<number>();

export const PresetReleaseTimeCtrl: React.FC<PresetReleaseTimeCtrlProps> = (
  props
) => {
  const timezone = new Date().getTimezoneOffset() / -60;
  const { time, onChange } = props;

  const originalHour = time?.hour || 1;
  const originalMinutes = time?.minutes || 0;
  const originalPeriod = time?.period || 'AM';

  const [hours, setHours] = React.useState(originalHour);
  const [minutes, setMinutes] = React.useState(originalMinutes);
  const [period, setPeriod] = React.useState<string>(originalPeriod);

  React.useEffect(() => {
    if (originalHour !== hours) {
      setHours(originalHour || 0);
    }
    if (originalMinutes !== minutes) {
      setMinutes(originalMinutes || 0);
    }
    if (originalPeriod !== period) {
      setPeriod(originalPeriod || 'AM');
    }
  }, [originalHour, originalMinutes, originalPeriod, hours, minutes, period]);

  const handleChange = <T,>(
    setter: (v: T) => void,
    key: keyof ReleaseTimePreset
  ) => (value: T) => {
    setter(value);

    const original = {
      hour: hours,
      minutes,
      period: period as ReleaseTimePreset['period'],
      timezone,
    };

    onChange({
      ...original,
      [key]: value,
    });
  };

  return (
    <ReleaseTimeWrapper className={props.className}>
      <NumberDropdownCtrl
        title="Hour"
        value={hours}
        options={HOURS}
        onChange={handleChange(setHours, 'hour')}
      />
      <NumberDropdownCtrl
        title="Minutes"
        value={minutes}
        options={MINUTES}
        onChange={handleChange(setMinutes, 'minutes')}
      />
      <DropdownCtrl
        title="Period"
        value={period}
        options={DAY_PERIODS}
        onChange={handleChange(setPeriod, 'period')}
      />
    </ReleaseTimeWrapper>
  );
};

const ReleaseTimeWrapper = styled(Flex)`
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;

  & > * {
    width: 100%;
    margin-bottom: 5px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media ${({ theme }) => theme.device.laptopS} {
    justify-content: space-between;
    flex-wrap: no-wrap;
    flex-direction: row;

    & > * {
      width: calc((100% - 10px) / 3);
      margin-bottom: 0;

      &:nth-child(2) {
        margin: 0 5px;
      }
    }
  }
`;
